/**
 * Invite User Dialog Component
 *
 * Modal dialog for inviting new users to the organization.
 */

import { useCallback,useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { useCreateInvitationMutation } from '../store/api';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableRoles?: ProviderRoleInfo[];
}

export function InviteUserDialog({
  open,
  onClose,
  onSuccess,
  availableRoles = [],
}: InviteUserDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createInvitation, { isLoading }] = useCreateInvitationMutation();

  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>(ROLES.CLINICIAN);

  const handleInvite = useCallback(async () => {
    if (!email.trim()) {
      enqueueSnackbar('Please enter an email address', { variant: 'warning' });
      return;
    }

    try {
      await createInvitation({
        providerInvitationRequest: {
          email: email.trim(),
          role: selectedRole,
        },
      }).unwrap();

      enqueueSnackbar('Invitation sent successfully', { variant: 'success' });
      setEmail('');
      setSelectedRole(ROLES.CLINICIAN);
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to send invitation',
        { variant: 'error' }
      );
    }
  }, [email, selectedRole, createInvitation, enqueueSnackbar, onSuccess]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setEmail('');
      setSelectedRole(ROLES.CLINICIAN);
      onClose();
    }
  }, [isLoading, onClose]);

  // Get available roles from API or use defaults
  const rolesToDisplay = availableRoles.length > 0
    ? availableRoles.filter(r => !r.role.includes('billing'))
    : [
        { role: ROLES.CLINICIAN, display_name: 'Clinician', description: 'Clinical staff member' },
        { role: ROLES.OPERATIONS_SUPPORT, display_name: 'Operations Support', description: 'Operations team member' },
        { role: ROLES.PRACTICE_ADMINISTRATOR, display_name: 'Practice Administrator', description: 'Practice admin with billing access' },
        { role: ROLES.SUPERVISOR, display_name: 'Supervisor', description: 'Clinical supervisor' },
      ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite New User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleInvite();
              }
            }}
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Role"
              disabled={isLoading}
            >
              {rolesToDisplay.map((role) => (
                <MenuItem key={role.role} value={role.role}>
                  {role.display_name || getRoleDisplayName(role.role)}
                  {role.description && (
                    <span style={{ marginLeft: '8px', fontSize: '0.875rem', color: 'text.secondary' }}>
                      - {role.description}
                    </span>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleInvite}
          color="primary"
          variant="contained"
          disabled={isLoading || !email.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}